import {Component, inject} from '@angular/core';
import {HeaderComponent} from '../common/header/header.component';
import {IonCol, IonContent, IonGrid, IonRow, IonSpinner,} from '@ionic/angular/standalone';
import {MatPaginatorModule} from '@angular/material/paginator';
import {ReactiveFormsModule} from '@angular/forms';
import {DashboardCounterComponent} from '../common/dashboard-counter/dashboard-counter.component';
import {DashboardPivotChartComponent} from '../common/dashboard-pivot-chart/dashboard-pivot-chart.component';
import {DashboardLogListComponent} from '../common/dashboard-log-list/dashboard-log-list.component';
import {ErrorClassification} from '../../enums/ErrorClassification';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DashboardService} from '../../services/dashboard/dashboard.service';
import {BaseError} from '../../models/error/BaseError';
import {DashboardHubService} from '../../services/websocket/dashboard-hub.service';
import {ErrorCountsByCategory} from '../../models/dashboard/ErrorCountsByCategory';
import {forkJoin, Observable, Subject, Subscription, switchMap, take, tap, timer} from 'rxjs';
import {LoadingSpinnerService} from '../../services/userInterface/loading-spinner.service';
import {ToastService} from '../../services/userInterface/toast.service';
import {
    AnimatedDashboardCounterComponent
} from '../common/animated-dashboard-counter/animated-dashboard-counter.component';
import {AuthService} from '../../services/auth/auth.service';
import {ErrorChartData} from '../../models/dashboard/ErrorChartData';
import {ErrorChartState} from '../../models/dashboard/ErrorChartState';
import {ChartLevel} from '../../enums/ChartLevel';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: true,
    imports: [
        HeaderComponent,
        DashboardCounterComponent,
        AnimatedDashboardCounterComponent,
        IonContent,
        IonCol,
        IonRow,
        MatPaginatorModule,
        ReactiveFormsModule,
        HeaderComponent,
        DashboardPivotChartComponent,
        DashboardLogListComponent,
        IonGrid,
        TranslatePipe
    ]
})
export class DashboardComponent {
    private static readonly MAX_AMOUNT_OF_ERRORS_IN_LOG_LIST: number = 10;
    private static readonly REFRESH_TIME_CHARTS_IN_MINUTES: number = 30;

    protected readonly ErrorClassification = ErrorClassification;

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly loadingSpinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);
    private readonly toastService: ToastService = inject(ToastService);

    private readonly dashboardService: DashboardService = inject(DashboardService);
    private readonly dashboardHub: DashboardHubService = inject(DashboardHubService);
    private readonly authService: AuthService = inject(AuthService);

    protected totalAmountOfTenants: number;
    protected totalAmountOfTenantApplications: number;

    protected errorCountsByCategory: ErrorCountsByCategory;
    private errorCountUpdatedSubscription?: Subscription;

    protected showErrorList: boolean;
    protected errors: BaseError[];
    protected loadingErrors: boolean;
    protected loadingDashboard: boolean;
    protected isSuperTenant: boolean;

    private currentErrorClassification?: ErrorClassification;
    private errorClassification: Subject<ErrorClassification>;
    private errorSubscription?: Subscription;

    private technicalErrorChartState: ErrorChartState;
    protected technicalChartData!: ErrorChartData;
    protected isTechnicalChartLoading: boolean;

    private functionalErrorChartState: ErrorChartState;
    protected functionalChartData!: ErrorChartData;
    protected isFunctionalChartLoading: boolean;

    private refreshChartSubscription?: Subscription;
    private refreshChartTimer: Observable<number>;

    constructor() {
        this.showErrorList = false;
        this.loadingErrors = false;
        this.loadingDashboard = false;
        this.isSuperTenant = false

        this.errors = [];
        this.errorCountsByCategory = new ErrorCountsByCategory(0, 0);
        this.errorClassification = new Subject<ErrorClassification>();

        this.totalAmountOfTenants = 0;
        this.totalAmountOfTenantApplications = 0;

        this.technicalErrorChartState = new ErrorChartState();
        this.isTechnicalChartLoading = false;
        this.functionalErrorChartState = new ErrorChartState();
        this.isFunctionalChartLoading = false;

        this.refreshChartTimer = timer(0, DashboardComponent.REFRESH_TIME_CHARTS_IN_MINUTES * 60 * 1000);
    }

    public async ngOnInit() {
        this.loadingDashboard = true;
        await this.initializeErrorCounters();
        this.initializeErrorFetching();
        await this.initializeCharts();
    }

    public async ngOnDestroy() {
        await this.cleanUpResources();
    }

    private async cleanUpResources() {
        await this.dashboardHub.stopHubConnection();
        this.errorCountUpdatedSubscription?.unsubscribe();
        this.errorSubscription?.unsubscribe();
        this.refreshChartSubscription?.unsubscribe();
        await this.loadingSpinnerService.hideSpinner();
    }

    private async initializeCharts() {
        this.refreshChartSubscription = this.refreshChartTimer.subscribe(async () => {
            this.isTechnicalChartLoading = true;
            this.isFunctionalChartLoading = true;

            await this.getTechnicalChartData();
            this.isTechnicalChartLoading = false;

            await this.getFunctionalChartData();
            this.isFunctionalChartLoading = false;
        });
    }

    private async initializeErrorCounters() {
        await this.loadingSpinnerService.showSpinner();
        this.isSuperTenant = this.authService.currentUserIsFromSuperTenant();
        await this.dashboardHub.initializeHubConnection();

        if (this.isSuperTenant) {
            this.getInitialValuesForAllCounters();
        } else {
            this.getInitialValuesForErrorCounters();
        }

        this.errorCountUpdatedSubscription = this.dashboardHub.errorCountIsUpdated().subscribe({
            next: async (errorCountsByCategory) => {
                this.errorCountsByCategory = errorCountsByCategory;
            },
        });
    }

    private getInitialValuesForAllCounters() {
        forkJoin({
            errorCountsByCategory: this.dashboardHub.errorCountIsInitialized().pipe(take(1)),
            totalAmountOfTenants: this.dashboardService.getTotalAmountOfTenants(),
            totalAmountOfTenantApplications: this.dashboardService.getTotalAmountOfTenantApplications(),
        }).subscribe({
            next: async ({ errorCountsByCategory, totalAmountOfTenants, totalAmountOfTenantApplications }) => {
                this.errorCountsByCategory = errorCountsByCategory;
                this.totalAmountOfTenants = totalAmountOfTenants;
                this.totalAmountOfTenantApplications = totalAmountOfTenantApplications;

                await this.finishDashboardLoading();
            },
            error: async (error) => {
                await this.handleDashboardLoadError();
            }
        });
    }

    private getInitialValuesForErrorCounters() {
        this.dashboardHub.errorCountIsInitialized().pipe(take(1)).subscribe({
            next: async (errorCountsByCategory) => {
                this.errorCountsByCategory = errorCountsByCategory;
                await this.finishDashboardLoading();
            },
            error: async (error) => {
                await this.handleDashboardLoadError();
            }
        });
    }

    private async finishDashboardLoading() {
        await this.loadingSpinnerService.hideSpinner();
        this.loadingDashboard = false;
    }

    private async handleDashboardLoadError() {
        await this.loadingSpinnerService.hideSpinner();
        this.loadingDashboard = false;

        const errorMessage: string = this.translateService.instant("toastMessage.error.dashboard.initialize");
        await this.toastService.showErrorMessage(errorMessage);
    }

    private initializeErrorFetching() {
        this.errorSubscription = this.errorClassification
            .pipe(
                tap(() => {
                    this.loadingErrors = true;
                    this.showErrorList = true;
                }),
                switchMap(classification => {
                    return classification === ErrorClassification.FunctionalError
                        ? this.dashboardService.getFunctionalErrors(DashboardComponent.MAX_AMOUNT_OF_ERRORS_IN_LOG_LIST)
                        : this.dashboardService.getTechnicalErrors(DashboardComponent.MAX_AMOUNT_OF_ERRORS_IN_LOG_LIST);
                })
            )
            .subscribe({
                next: errors => {
                    this.errors = errors;
                    this.loadingErrors = false;
                },
                error: async () => {
                    this.errors = [];
                    this.loadingErrors = false;
                    await this.toastService.showErrorMessage();
                }
            });
    }

    protected onChangeErrorClassification = (errorClassification: ErrorClassification) => {
        if (errorClassification == this.currentErrorClassification) {
            this.showErrorList = false;
            this.currentErrorClassification = undefined;
            this.errors = [];
            return;
        }

        this.currentErrorClassification = errorClassification;
        this.errorClassification.next(errorClassification);
    }

    protected get logListTitle(): string {
        let messageKey: string;
        switch (this.currentErrorClassification) {
            case ErrorClassification.FunctionalError:
                messageKey = "error.category.functional";
                break;
            case ErrorClassification.TechnicalError:
                messageKey = "error.category.technical";
                break;
            default:
                messageKey = "error.category.unknown";
                break;
        }

        return this.translateService.instant(messageKey);
    }

    protected get technicalChartCanZoomOut(): boolean {
        return this.technicalErrorChartState.level != ChartLevel.Month;
    }

    protected async onTechnicalZoomIn(newValue: number) {
        this.isTechnicalChartLoading = true;
        const newState: ErrorChartState = this.computeNextState(this.technicalErrorChartState, newValue);
        await this.changeTechnicalState(newState);
        this.isTechnicalChartLoading = false;
    }

    protected async onTechnicalZoomOut() {
        this.isTechnicalChartLoading = true;
        const newState: ErrorChartState = this.computePreviousState(this.technicalErrorChartState);
        await this.changeTechnicalState(newState);
        this.isTechnicalChartLoading = false;
    }

    private async changeTechnicalState(newState: ErrorChartState) {
        if(newState.level != this.technicalErrorChartState.level) {
            this.technicalErrorChartState = newState;
            await this.getTechnicalChartData();
        }
    }

    private async getTechnicalChartData() {
        try {
            this.technicalChartData = await this.dashboardHub.getTechnicalErrorChartData(this.technicalErrorChartState);
        } catch (e) {
            const errorMessage: string = this.translateService.instant("toastMessage.error.dashboard.technicalChartData");
            await this.toastService.showErrorMessage(errorMessage);
        }
    }

    private computeNextState(currentState: ErrorChartState, newValue: number): ErrorChartState {
        switch (currentState.level) {
            case ChartLevel.Month:
                return new ErrorChartState(ChartLevel.Week, newValue);
            case ChartLevel.Week:
                return new ErrorChartState(ChartLevel.Day, currentState.month, newValue);
            case ChartLevel.Day:
                return currentState;
        }
    }

    private computePreviousState(currentState: ErrorChartState): ErrorChartState {
        switch (currentState.level) {
            case ChartLevel.Day:
                return new ErrorChartState(ChartLevel.Week, currentState.month);
            case ChartLevel.Week:
                return new ErrorChartState(ChartLevel.Month);
            default:
                return currentState;
        }
    }

    protected get functionalChartCanZoomOut(): boolean {
        return this.functionalErrorChartState.level != ChartLevel.Month;
    }

    protected async onFunctionalZoomIn(newValue: number) {
        this.isFunctionalChartLoading = true;
        const newState: ErrorChartState = this.computeNextState(this.functionalErrorChartState, newValue);
        await this.changeFunctionalState(newState);
        this.isFunctionalChartLoading = false;
    }

    protected async onFunctionalZoomOut() {
        this.isFunctionalChartLoading = true;
        const newState: ErrorChartState = this.computePreviousState(this.functionalErrorChartState);
        await this.changeFunctionalState(newState);
        this.isFunctionalChartLoading = false;
    }

    private async changeFunctionalState(newState: ErrorChartState) {
        if(newState.level != this.functionalErrorChartState.level) {
            this.functionalErrorChartState = newState;
            await this.getFunctionalChartData();
        }
    }

    private async getFunctionalChartData() {
        try {
            this.functionalChartData = await this.dashboardHub.getFunctionalErrorChartData(this.functionalErrorChartState);
        } catch (e) {
            const errorMessage: string = this.translateService.instant("toastMessage.error.dashboard.functionalChartData");
            await this.toastService.showErrorMessage(errorMessage);
        }
    }
}
